import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import capitalize from '@material-ui/core/utils/capitalize';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import styled from 'styled-components';
import { useGetContractProductDates, usePostContract } from '../hooks/api/contractsApi';
import QuantityChart from '../../../components/QuantityChart/QuantityChart';
import ContentLoader from '../../../components/ContentLoader';
import CardRow from '../../../components/CardRow/CardRow';
import CardRowCard from '../../../components/CardRow/CardRowCard';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';
import { BLACK, GRAY1, GRAY3 } from '../../../constants/colors';
import { getMonthName } from '../../../utils/date';
import { useHistory } from 'react-router';
import useAppContext from '../../../hooks/context/useAppContext';
import userRoles from '../../../constants/userRoles';
import { commodities } from '../../../constants/commodities';
import { useShowGeneraSnackbar } from '../../../components/Snackbar/SnackbarManager';
import { useContractConstants } from '../hooks/contractConstants';

const contractEditLink = 'contracts';
const basicInfoLink = 'basic-information';
const volumesLink = 'volumes';
const paramsLink = 'params';


const StyledTableCell = styled(TableCell)`
  border-bottom: 0;
  padding-top: 0;
  padding-bottom: 8px;
  vertical-align: top;
`;

const StyledTableCellHead = styled(TableCell)`
  border-bottom: 0;
  padding-top: 0;
  padding-bottom: 8px;
  padding-left: 0;
  vertical-align: top;
  color: ${GRAY1};
`;

const StyledTableCellBorder = styled(TableCell)`
  vertical-align: top;
`;

const StyledTableCellHeadBorder = styled(TableCell)`
  padding-bottom: 16px;
  padding-top: 16px;
  vertical-align: middle;
  font-weight: bold;
  color: ${BLACK};
  border-top: 1px solid ${GRAY3};
`;

const StyledTextarea = styled(TextareaAutosize)`
  width: 100%;
  padding: 20px;
  font-family: inherit;
`;

const StyledError = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  font-size: 12px;
`;

const StyledHeadline = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  button {
    margin-top: -10px;
  }
`;

const displayMargin = (margin) => {
  return margin ? `margin: ${margin}px;` : '';
};

const StyledHeadlineTable = styled.h3`
  margin-top: 0;
  font-weight: normal;
  ${({ margin }) => displayMargin(margin)}
`;


let tariffsToShow = [];

const ContractParameters = ({ match, contractData }) => {
  const intl = useIntl();

  const TRANSLATION_BASE = contractData.isFix ? 'app.contract.sale' : 'app.contract.purchase';
  const history = useHistory();
  const { getContractProductDates, loading: loadingContractProductDates, data: contractProductDatesData } = useGetContractProductDates();
  const { postContract, data: commentData, error: commentError } = usePostContract();
  const { handleSubmit, errors, control } = useForm();
  const { tariffs: contractTariffs, purchaseIndexType } = useContractConstants();
  const [isAdmin, setIsAdmin] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [tariffs, setTariffs] = useState(contractTariffs);
  const { showSnackbarGeneralError, showSnackbarSuccess } = useShowGeneraSnackbar();

  const onSubmitNote = async (e) => {
    await postContract(match.params.contractId, {
      comment: e?.note || '',
      adminComment: e?.adminNote || '',
    });
  };

  useEffect(() => {
    setTariffs(contractTariffs);
    // eslint-disable-next-line
  }, [intl]);

  useEffect(() => {
    if (commentData != null) {
      contractData.comment = commentData?.comment || '';
      contractData.adminComment = commentData?.adminComment || '';
      showSnackbarSuccess(intl.formatMessage({ id: 'app.common.noteSaved' }));
    }
  }, [commentData, contractData, showSnackbarSuccess, intl]);

  useEffect(() => {
    if (commentError != null) {
      showSnackbarGeneralError(intl.formatMessage({ id: 'app.common.noteSaveError' }));
    }
  }, [commentError, showSnackbarGeneralError, intl]);


  // local state
  const [basicInfo, setBasicInfo] = useState([]);
  const { user } = useAppContext();
  const [gradualPurchase, setGradualPurchase] = useState([]);
  const [purchaseAvailableDates, setPurchaseAvailableDates] = useState([]);
  const [quantityChartData, setQuantityChartData] = useState([]);
  const [tariffData, setTariffData] = useState([]);
  const [formError, setFormError] = useState(null);
  const [refPrices, setRefPrices] = useState(null);
  const referencePriceTitle = intl.formatMessage({ id: 'app.contractParameters.referencePriceTitle' });

  useEffect(() => {
    const permittedUsers = [userRoles.CUSTOMER, userRoles.KAM, userRoles.ASM, userRoles.ADMIN, userRoles.CUSTOMER_VIEWER,
      userRoles.CUSTOMER_MULTIPLE_COTRACTS, userRoles.CUSTOMER_VIEWER_MULTIPLE_COTRACTS];
    if (permittedUsers.includes(user.role)) {
      setReadOnly(false);
    }
  }, [setReadOnly, user.role]);

  useEffect(() => {
    if (errors) {
      if (errors.note) {
        if (errors.note.type === 'required') {
          setFormError(intl.formatMessage({ id: 'app.contractParameters.requiredError' }));
        }
        if (errors.note.type === 'maxLength') {
          setFormError(intl.formatMessage({ id: 'app.contractParameters.maxLengthError' }));
        }
      }
    }
  }, [errors, intl]);

  const { getUser } = useAppContext();

  useEffect(() => {
    const loggedUser = getUser();
    loggedUser && loggedUser?.role === 'ADMIN' && setIsAdmin(true);
  }, [getUser]);

  useEffect(() => {
    const prices = [];
    if (contractData && contractData?.purchaseIndexType?.id === 'RP') {
      for (let i = 0; i < tariffData?.length; i++) {
        prices.push({ id: i, value: contractData?.referencePrice });
      }
    }
    prices.length === 0 ? setRefPrices(null) : setRefPrices(prices);
  }, [contractData, tariffData]);

  useEffect(() => {
    (async () => {
      await getContractProductDates(match.params.contractId);
    })();
  }, [getContractProductDates, match.params.contractId]);

  useEffect(() => {
    if (contractProductDatesData) {
      setPurchaseAvailableDates(() =>
        contractProductDatesData.map((item) => {
          return {
            product: item.name,
            date: intl.formatDate(new Date(item.buyTill)),
            isAvailable: item.isTradable ? intl.formatMessage({ id: 'app.yes' }) : intl.formatMessage({ id: 'app.no' }),
            alert: intl.formatMessage({ id: 'app.contractParameters.contractProductDatesData.alert' }),
          };
        }),
      );
    }
  }, [contractProductDatesData, intl]);

  useEffect(() => {
    if (contractData) {
      const gradualPurchaseSum = contractData.quantities.reduce((total, item) => total + item.quantity, 0);

      const parameterArray = [
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.customer' }),
          value: contractData.customer.name,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.idExternal' }),
          value: contractData.idExternal,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.frameContract' }),
          value: contractData.frameContract,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.commodity' }),
          value: contractData.commodity.name,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.contractPeriod' }),
          value: contractData.contractPeriod,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.currency' }),
          value: `${contractData.currency.name} (${contractData.currency.id})`,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.invoicingCurrency' }),
          value: `${contractData.invoicingCurrency.name} (${contractData.invoicingCurrency.id})`,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.supplyProduct' }),
          value: contractData.supplyProduct.name,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.gradualPurchaseSum' }),
          value: `${intl.formatNumber(gradualPurchaseSum)} ${commodities[contractData.commodity.id].unit}`,
        },
      ];

      if (contractData.kspotPlus != null) {
        parameterArray.push(
          {
            label: intl.formatMessage({ id: 'app.contractParameters.contractData.kSpotPlus' }),
            value: contractData.kspotPlus,
          },
        );
      }

      if (contractData.kspotMinus != null) {
        parameterArray.push(
          {
            label: intl.formatMessage({ id: 'app.contractParameters.contractData.kSpotMinus' }),
            value: contractData.kspotMinus,
          },
        );
      }

      setBasicInfo(parameterArray);

      setGradualPurchase([
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.purchaseIndexType' }),
          value: contractData.purchaseIndexType.name,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.stockExchangeProduct' }),
          value: contractData.isMonthlyPurchaseAvailable ? intl.formatMessage({ id: 'app.contractParameters.contractData.isMonthlyPurchaseAvailable' }) :
            intl.formatMessage({ id: 'app.contractParameters.contractData.availableForYear' }),
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.quantity' }),
          value: `${intl.formatNumber(contractData.quantity)} ${commodities[contractData.commodity.id].unit}`,
        },
        {
          label: intl.formatMessage({ id: `${TRANSLATION_BASE}.tradingSteps` }),
          value: contractData.maxPurchaseCount,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.purchaseMethod' }),
          value: contractData.purchaseMethod.name,
        },
        {
          label: intl.formatMessage({ id: 'app.contractParameters.contractData.buyBack' }),
          value: contractData.isSaleAvailable ? intl.formatMessage({ id: 'app.yes' }) : intl.formatMessage({ id: 'app.no' }),
        },
      ]);

      setQuantityChartData(() =>
        contractData.quantities.map((item) => {
          return {
            month: capitalize(getMonthName(intl, new Date(contractData.contractPeriod, item.month - 1, 1))),
            quantity: `${intl.formatNumber(item.quantity)}\u00A0${commodities[contractData.commodity.id].unit}`,
            value: item.quantity,
          };
        }),
      );

      tariffsToShow = [...tariffs];
      const result = [];
      tariffsToShow = [...tariffs];
      contractData.indexValues.forEach((item) => {
        const resultMatch = result.filter((resultItem) => resultItem.idTariff === item.idTariff);
        if (resultMatch.length) {
          item.purchaseIndexType.id === purchaseIndexType.RIK ?
            resultMatch[0][item.purchaseIndexType.id][item.priceSource.id] = `${item.value}/${item?.value2}` :
            resultMatch[0][item.purchaseIndexType.id][item.priceSource.id] = item.value;
        } else {
          const resultItem = {
            idTariff: item.idTariff,
            title: `Tarif ${item.idTariff}`,
          };
          resultItem[item.purchaseIndexType.id] = {};

          item.purchaseIndexType.id === purchaseIndexType.RIK ?
            resultItem[item.purchaseIndexType.id][item.priceSource.id] = `${item.value}/${item?.value2}` :
            resultItem[item.purchaseIndexType.id][item.priceSource.id] = item.value;
          result.push(resultItem);
        }

        tariffsToShow = tariffsToShow.map((tariff) => {
          let tariffResult;
          if (tariff.purchaseIndexType === item.purchaseIndexType.id && tariff.priceSource === item.priceSource.id) {
            tariffResult = {
              ...tariff,
              isVisibleInTable: true,
            };
          } else {
            tariffResult = tariff;
          }
          return tariffResult;
        });
      });

      setTariffData(result);
    }
  }, [contractData, intl, TRANSLATION_BASE, tariffs, purchaseIndexType.RIK]);


  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs>
          <CardRow>
            <CardRowCard>
              <Box p={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <StyledHeadlineTable>{intl.formatMessage({ id: 'app.contractParameters.basicData' })}</StyledHeadlineTable>
                    {!contractData && <ContentLoader type={'PARAGRAPH'} />}
                    {contractData && (
                      <TableContainer>
                        <Table aria-label="simple table">
                          <TableBody>
                            {basicInfo.map((item, key) => (
                              <TableRow key={key}>
                                <StyledTableCellHead scope="row">{item.label}</StyledTableCellHead>
                                <StyledTableCell>{item.value}</StyledTableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StyledHeadlineTable>{intl.formatMessage({ id: `${TRANSLATION_BASE}.gradualTrade` })}</StyledHeadlineTable>
                    {!contractData && <ContentLoader type={'PARAGRAPH'} />}
                    {contractData && (
                      <TableContainer>
                        <Table aria-label="simple table">
                          <TableBody>
                            {gradualPurchase.map((item, key) => (
                              <TableRow key={key}>
                                <StyledTableCellHead component="th" scope="row">
                                  {item.label}
                                </StyledTableCellHead>
                                <StyledTableCell>{item.value}</StyledTableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <form onSubmit={(e) => e.preventDefault()}>
                      <Grid container spacing={1} justify="flex-end">
                        <Grid item xs={12} align="right">
                          <IconButton
                            onClick={() => {
                              history.push(`/${contractEditLink}/${match.params.contractId}/${basicInfoLink}`);
                            }}>
                            {isAdmin && <EditIcon />}
                          </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            as={StyledTextarea}
                            name="note"
                            defaultValue={contractData?.comment ? contractData?.comment : undefined}
                            control={control}
                            readOnly={readOnly}
                            placeholder={intl.formatMessage({ id: 'app.common.note' })}
                            rowsMin={6}
                            rules={{ required: false, maxLength: 400 }}
                          />
                          {formError && <StyledError>{formError}</StyledError>}
                        </Grid>
                        <Grid item xs={12} align="right">
                          {(!readOnly && !isAdmin) && (
                            <Button onClick={handleSubmit(onSubmitNote)} variant="outlined">
                              <FormattedMessage id={'app.save'} />
                            </Button>
                          )}
                        </Grid>
                        {isAdmin && (
                          <>
                            <Grid item xs={12}>
                              <Controller
                                as={StyledTextarea}
                                name="adminNote"
                                defaultValue={contractData?.adminComment ? contractData?.adminComment : undefined}
                                control={control}
                                readOnly={readOnly}
                                placeholder={intl.formatMessage({ id: 'app.contractParameters.adminComment' })}
                                rowsMin={6}
                                rules={{ required: false }}
                              />
                              {formError && <StyledError>{formError}</StyledError>}
                            </Grid>
                            <Grid item xs={12} align="right">
                              <Button onClick={handleSubmit(onSubmitNote)} variant="outlined">
                                <FormattedMessage id={'app.save'} />
                              </Button>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </form>
                  </Grid>
                </Grid>
              </Box>
            </CardRowCard>
          </CardRow>
        </Grid>
      </Grid>
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <CardRow>
              <CardRowCard>
                <Box p={2}>
                  <StyledHeadline>
                    <StyledHeadlineTable>{intl.formatMessage({ id: 'app.contractParameters.deliveryVolumes' })}</StyledHeadlineTable>
                    {isAdmin && (
                      <IconButton
                        onClick={() => {
                          history.push(`/${contractEditLink}/${match.params.contractId}/${volumesLink}`);
                        }}>
                        <EditIcon />
                      </IconButton>
                    )}
                  </StyledHeadline>
                  {!contractData && <ContentLoader type={'PARAGRAPH'} />}
                  {contractData && <QuantityChart chartData={quantityChartData} />}
                </Box>
              </CardRowCard>
            </CardRow>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardRow>
              <CardRowCard>
                <Box p={2}>
                  <StyledHeadlineTable margin={0}>{intl.formatMessage({ id: `${TRANSLATION_BASE}.deadlines` })}</StyledHeadlineTable>
                </Box>
                {loadingContractProductDates && (
                  <Box p={2}>
                    <ContentLoader type={'PARAGRAPH'} />
                  </Box>
                )}
                {contractProductDatesData && (
                  <TableContainer>
                    <Table aria-label="simple table">
                      <TableBody>
                        <TableRow>
                          <StyledTableCellHeadBorder>{intl.formatMessage({ id: 'app.contractParameters.product' })}</StyledTableCellHeadBorder>
                          <StyledTableCellHeadBorder>{intl.formatMessage({ id: 'app.contractParameters.purchasedUntil' })}</StyledTableCellHeadBorder>
                          <StyledTableCellHeadBorder>{intl.formatMessage({ id: 'app.contractParameters.warnBeforeEnd' })}</StyledTableCellHeadBorder>
                        </TableRow>
                        {purchaseAvailableDates.map((item, key) => (
                          <TableRow key={key}>
                            <StyledTableCellBorder>{item.product}</StyledTableCellBorder>
                            <StyledTableCellBorder>{item.date}</StyledTableCellBorder>
                            <StyledTableCellBorder>{item.alert}</StyledTableCellBorder>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardRowCard>
            </CardRow>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionHeader>{intl.formatMessage({ id: `${TRANSLATION_BASE}.gradualTradeParameters` })}</SectionHeader>
            <CardRow>
              <CardRowCard>
                {!contractData && (
                  <Box p={2}>
                    <ContentLoader type={'PARAGRAPH'} />
                  </Box>
                )}
                {contractData && (
                  <>
                    {isAdmin && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} align="right">
                          <Box mr={2} mb={1}>
                            <IconButton
                              onClick={() => {
                                history.push(`/${contractEditLink}/${match.params.contractId}/${paramsLink}`);
                              }}>
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    <TableContainer>
                      <Table aria-label="simple table">
                        <TableBody>
                          <TableRow>
                            <StyledTableCellHeadBorder />
                            {tariffData.map((item, key) => (
                              <StyledTableCellHeadBorder key={key}>{item.title}</StyledTableCellHeadBorder>
                            ))}
                          </TableRow>
                          {refPrices && (
                            <TableRow key={referencePriceTitle}>
                              <StyledTableCellBorder>{referencePriceTitle}</StyledTableCellBorder>
                              {refPrices.map((item) => (
                                <StyledTableCellBorder key={item.id}>{item.value}</StyledTableCellBorder>
                              ))}
                            </TableRow>
                          )}
                          {tariffsToShow
                            .filter((tariff) => tariff.isVisibleInTable)
                            .map((tariff, tariffKey) => (
                              <TableRow key={tariffKey}>
                                <StyledTableCellBorder>
                                  { tariff.label }
                                </StyledTableCellBorder>

                                {tariffData.map((tariffDataItem, tariffDataKey) => (
                                  <StyledTableCellBorder key={tariffDataKey}>
                                    {tariffDataItem?.[tariff.purchaseIndexType]?.[tariff.priceSource]}
                                  </StyledTableCellBorder>
                                ))}
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardRowCard>
            </CardRow>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

ContractParameters.propTypes = {
  match: PropTypes.object.isRequired,
  contractData: PropTypes.object,
};

export default ContractParameters;
